import { generateBreadcrumbList } from '../utils';
import { SCHEMA_CTX } from '../constants';

/**
 * Generates structured data for breadcrumbs on a webpage.
 *
 * @param {Object} params - The parameters for generating breadcrumb structured data.
 * @param {string} params.canonical - The canonical URL of the webpage.
 * @param {Array<Object>} [params.breadcrumbs=[]] - An array of breadcrumb objects, each containing:
 *   - `name` (string): The label of the breadcrumb.
 *   - `id` (string): The URL or identifier for the breadcrumb.
 * @param {string} params.title - The title of the webpage.
 * @param {string} [params.brandName] - The name of the brand (optional).
 * @param {string} [params.brandLinkUrl] - The URL for the brand (optional).
 * @returns {Object} The structured data object for breadcrumbs, including:
 *   - `@context` (string): The schema.org context.
 *   - `@type` (string): The type of the structured data (WebPage).
 *   - `name` (string): The title of the webpage.
 *   - `url` (string): The canonical URL of the webpage.
 *   - `breadcrumb` (Object): The breadcrumb list generated by `generateBreadcrumbList`.
 */
export const getBreadcrumbStructuredData = ({
  canonical,
  breadcrumbs = [],
  title,
  brandName,
  brandLinkUrl,
}) => {
  let breadcrumbItems = [...breadcrumbs];

  // Add branded breadcrumb if brand info is available
  if (brandName && brandLinkUrl) {
    const lastCrumb = breadcrumbItems[breadcrumbItems.length - 1];
    breadcrumbItems.push({
      name: `${brandName} ${lastCrumb?.label || lastCrumb?.name || ''}`.trim(),
      id: brandLinkUrl,
    });
  }

  return {
    '@context': SCHEMA_CTX,
    '@type': 'WebPage',
    name: title,
    url: canonical,
    breadcrumb: generateBreadcrumbList(breadcrumbItems, { browse: true })
  };
};
